import { useEffect, useState } from 'react';
import { useFile } from '../../context/FileContext';
import { List, CodeXml, Share, Check } from 'lucide-react';
import CodeBlock from '../blocks/CodeBlock';
import DeclarationImplementationBlock from '../blocks/DeclarationImplementationBlock';
import PropertyBlock from '../blocks/PropertyBlock';
import EventTableBlock from '../blocks/EventTableBlock';

/**
 * Main content display area - shows file contents with parsing options
 */
const Content = () => {
  const { content, fileName, filePath, isLoading, source } = useFile();
  const [displayRawToggle, setDisplayRawToggle] = useState(false);
  const [isParsed, setIsParsed] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setIsParsed(
      content.declaration ||
      content.implementation ||
      content.methods ||
      content.properties
    );
  }, [content]);

  const handleShare = async () => {
    const url = window.location.href;

    try {
      // Try using the modern Clipboard API
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        // Fallback for older browsers, phones, or non-secure contexts (http)
        const textArea = document.createElement('textarea');
        textArea.value = url;
        // Ensure textarea is not visible but part of DOM
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        textArea.style.top = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const renderContent = () => {
    if (isLoading) return <div>Loading...</div>;
    if (!content) return <div>Select a file</div>;

    if (displayRawToggle) return <CodeBlock code={content.xmlString} />;

    if (isParsed) {
      return (
        <>
          <DeclarationImplementationBlock
            declaration={content.declaration}
            implementation={content.implementation}
          />

          {content.methods &&
            content.methods.length > 0 &&
            content.methods.map((method, index) => (
              <DeclarationImplementationBlock
                key={index}
                title={method.name + '()'}
                declaration={method.declaration}
                implementation={method.implementation}
              />
            ))}

          {content.properties &&
            content.properties.length > 0 &&
            content.properties.map((property, index) => (
              <div key={index}>
                <PropertyBlock property={property} />
              </div>
            ))}

          <EventTableBlock events={content.events} translations={content.translations} />
        </>
      );
    }

    return <CodeBlock code={content.xmlString} />;
  };

  if (!content)
    return (
      <div className="Content">
        <h1>TwinCAT Online Viewer</h1>
      </div>
    );

  return (
    <div className="Content">
      <p>{filePath}</p>
      <div className="ContentHeader">
        <h2>{fileName}</h2>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          {source === 'github' && (
            <button
              onClick={handleShare}
              className={`share-btn ${copied ? 'copied' : ''}`}
              title="Copy shareable link to clipboard"
            >
              {copied ? <Check size={18} color="#38b2ac" /> : <Share size={18} />}
              <span>{copied ? 'Copied!' : 'Share URL to this file'}</span>
            </button>
          )}
          {isParsed && (
            <div className="button-group">
              <button
                onClick={() => setDisplayRawToggle(false)}
                className={displayRawToggle ? '' : 'active'}
              >
                <List size={20} />
              </button>
              <button
                onClick={() => setDisplayRawToggle(true)}
                className={displayRawToggle ? 'active' : ''}
              >
                <CodeXml size={20} />
              </button>
            </div>
          )}
        </div>
      </div>
      <hr />
      {renderContent()}
    </div>
  );
};

export default Content;
